<script setup lang="ts">
const { consent, showModal, acceptAll, rejectAll, savePreferences } = useCookieConsent()
const settingsStore = useSettingsStore()
const route = useRoute()

// Visitors must be able to read the privacy policy before making their choice.
const requiresChoice = computed(() => settingsStore.settings.cookie_consent_display === 'modal'
  && !route.path.replace(/\/$/, '').endsWith('/page/privacy'))

const drawerVisible = ref(false)

const analytics = ref(false)
const marketing = ref(false)

function syncFromCookie() {
  analytics.value = !!consent.value.analytics
  marketing.value = !!consent.value.marketing
}

const promptVisible = computed(() => showModal.value && !drawerVisible.value)
const bannerVisible = computed(() => promptVisible.value && !requiresChoice.value)
const dialogVisible = computed(() => promptVisible.value && requiresChoice.value)

watch(
  () => showModal.value,
  (open) => {
    if (open) syncFromCookie()
    // ако вече има решение, затваряме drawer-а
    if (!open) drawerVisible.value = false
  },
  { immediate: true }
)

function openSettings() {
  syncFromCookie()
  drawerVisible.value = true
}


function onAcceptAll() {
  acceptAll()
  drawerVisible.value = false
}

function onRejectAll() {
  rejectAll()
  drawerVisible.value = false
}

function onSave() {
  savePreferences({ analytics: analytics.value, marketing: marketing.value })
  drawerVisible.value = false
}
</script>

<template>
  <teleport to="body">
    <!-- Bottom banner -->
    <transition name="cc-slide">
      <div v-if="bannerVisible" class="cc-wrap" role="region" :aria-label="$t('cookie.aria.region')">
        <el-card shadow="never" class="cc-card py-3 px-3">

          <div class="cc-grid">
            <div class="cc-copy">
              <div class="cc-title">{{ $t('cookie.title') }}</div>
              <div class="cc-desc">
                {{ $t('cookie.bannerText') }}
                <NuxtLinkLocale to="/page/privacy" class="cc-link">
                  {{ $t('cookie.privacyLink') }}
                </NuxtLinkLocale>
              </div>
            </div>

            <div class="cc-actions">
              <el-button round size="small" @click="onRejectAll">{{ $t('cookie.actions.necessaryOnly') }}</el-button>
              <el-button round size="small" @click="openSettings">{{ $t('cookie.actions.settings') }}</el-button>
              <el-button class="cc-accept" type="primary" size="small" round @click="onAcceptAll">{{ $t('cookie.actions.acceptAll') }}</el-button>

            </div>
          </div>
        </el-card>
      </div>
    </transition>

    <el-dialog
      :model-value="dialogVisible"
      :title="$t('cookie.modalTitle')"
      class="cc-dialog"
      modal-class="cc-overlay"
      width="min(640px, calc(100vw - 32px))"
      align-center
      append-to-body
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :lock-scroll="true"
      :z-index="2000000002"
    >
      <template #header="{ titleId }">
        <div class="cc-modal-heading">
          <span class="cc-modal-symbol" aria-hidden="true">
            <Icon name="material-symbols-light:cookie-outline" size="30" />
          </span>
          <h2 :id="titleId" class="cc-modal-title">{{ $t('cookie.modalTitle') }}</h2>
        </div>
      </template>
      <p class="cc-modal-copy">{{ $t('cookie.modalText') }}</p>
      <template #footer>
        <div class="cc-modal-actions">
          <el-button class="cc-modal-accept" type="primary" @click="onAcceptAll">
            <span>{{ $t('cookie.actions.acceptAll') }}</span>
            <Icon name="material-symbols-light:arrow-forward-rounded" size="22" aria-hidden="true" />
          </el-button>
          <el-button class="cc-modal-settings" @click="openSettings">{{ $t('cookie.actions.settings') }}</el-button>
          <el-button class="cc-modal-necessary" text @click="onRejectAll">{{ $t('cookie.actions.necessaryOnly') }}</el-button>
        </div>
        <div class="cc-modal-privacy">
          <NuxtLinkLocale to="/page/privacy">
            <span>{{ $t('cookie.privacyLink') }}</span>
          </NuxtLinkLocale>
        </div>
      </template>
    </el-dialog>

    <el-drawer
      v-model="drawerVisible"
      :title="$t('cookie.settingsTitle')"
      direction="btt"
      size="min(520px, 90vh)"
      append-to-body
      :modal="true"
      :show-close="!requiresChoice"
      :close-on-click-modal="!requiresChoice"
      :close-on-press-escape="!requiresChoice"
      :z-index="2000000002"
      class="cc-drawer"
    >
      <div class="cc-drawer-body">
        <div class="cc-setting">
          <div class="cc-setting__row">
            <div class="cc-setting__name">{{ $t('cookie.essential.title') }}</div>
            <el-tag type="info" size="small">{{ $t('cookie.essential.badge') }}</el-tag>
          </div>
          <div class="cc-setting__desc">{{ $t('cookie.essential.description') }}</div>
        </div>

        <div class="cc-setting">
          <div class="cc-setting__row">
            <div class="cc-setting__name">{{ $t('cookie.analytics.title') }}</div>
            <el-switch v-model="analytics" :aria-label="$t('cookie.analytics.title')" />
          </div>
          <div class="cc-setting__desc">{{ $t('cookie.analytics.description') }}</div>
        </div>

        <div class="cc-setting">
          <div class="cc-setting__row">
            <div class="cc-setting__name">{{ $t('cookie.marketing.title') }}</div>
            <el-switch v-model="marketing" :aria-label="$t('cookie.marketing.title')" />
          </div>
          <div class="cc-setting__desc">{{ $t('cookie.marketing.description') }}</div>
        </div>

      </div>
      <template #footer>
        <div class="cc-drawer-actions">
          <el-button round @click="onRejectAll">{{ $t('cookie.actions.necessaryOnly') }}</el-button>
          <el-button round @click="onSave">{{ $t('cookie.actions.save') }}</el-button>
          <el-button class="cc-accept" type="primary" round @click="onAcceptAll">{{ $t('cookie.actions.acceptAll') }}</el-button>
        </div>
      </template>
    </el-drawer>
  </teleport>
</template>

<style scoped>
:deep(.el-card__body) {
  padding: 0;
  overflow: hidden;
}
/* Banner */
.cc-wrap {
  position: fixed;
width: 90%;
  left: 5%;
  right: 5%;


  bottom: calc(12px + env(safe-area-inset-bottom));
  z-index: 2000000001;
}
.cc-card {
  border-radius: 16px;
  border: 1px solid var(--el-border-color);
  background: var(--el-bg-color);
}
.cc-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
}
.cc-title {
  font-size: 0.95rem;
  font-weight: var(--fw-bold);
  margin-bottom: 4px;
}
.cc-desc { font-size: var(--fs--2); opacity: 1; line-height: 1.25; font-weight: var(--fw-semibold) }
.cc-link { text-decoration: underline; margin-left: 6px; }
.cc-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
.cc-actions :deep(.el-button), .cc-drawer-actions :deep(.el-button) { margin-left: 0; }
.cc-modal-copy { margin: 0; color: var(--el-text-color-regular); font-size: 15px; line-height: 1.75; }

/* Required choice */
:global(.cc-overlay) {
  background: rgb(15 23 42 / 40%);
  backdrop-filter: blur(3px);
}
:global(.cc-dialog) {
  --el-dialog-padding-primary: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 32px);
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--el-border-color-extra-light);
  border-radius: 20px;
  background: var(--el-bg-color);
  box-shadow: 0 24px 80px rgb(15 23 42 / 20%);
}
:global(.cc-dialog .el-dialog__header) { margin: 0; padding: 32px 32px 20px; }
:global(.cc-dialog .el-dialog__body) { min-height: 0; padding: 0 32px 28px; overflow-y: auto; }
:global(.cc-dialog .el-dialog__footer) { padding: 24px 32px 20px; border-top: 1px solid var(--el-border-color-extra-light); text-align: left; }
.cc-modal-heading { display: flex; align-items: center; gap: 16px; }
.cc-modal-symbol {
  display: grid;
  place-items: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}
.cc-modal-title { margin: 0; color: var(--el-text-color-primary); font-size: 25px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.3; }
.cc-modal-actions { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 10px; }
.cc-modal-actions :deep(.el-button) { min-width: 0; height: 46px; margin: 0; padding: 0 18px; border-radius: 10px; font-size: 13px; font-weight: 600; }
.cc-modal-actions :deep(.el-button > span) { display: flex; align-items: center; justify-content: center; gap: 14px; }
.cc-modal-actions :deep(.cc-modal-accept) { grid-column: 3; grid-row: 1; font-size: 14px; }
.cc-modal-actions :deep(.cc-modal-settings) { grid-column: 2; grid-row: 1; }
.cc-modal-actions :deep(.cc-modal-necessary) { grid-column: 1; grid-row: 1; justify-self: start; padding: 0 4px; color: var(--el-text-color-regular); }
.cc-modal-privacy { display: flex; justify-content: center; margin-top: 20px; }
.cc-modal-privacy a { color: var(--el-text-color-secondary); font-size: 12px; text-decoration: underline; text-underline-offset: 3px; }
.cc-modal-privacy a:hover { color: var(--el-color-primary); }
.cc-modal-privacy a:focus-visible { outline: 2px solid var(--el-color-primary); outline-offset: 5px; border-radius: 4px; }

/* Drawer (bottom sheet feel) */
:global(.cc-drawer) {
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
}
:global(.cc-drawer .el-drawer__header) {
  margin-bottom: 0;
  padding: 24px 24px 16px;
  color: var(--el-text-color-primary);
  font-weight: 600;
}
:global(.cc-drawer .el-drawer__body) {
  padding: 8px 24px;
}
:global(.cc-drawer .el-drawer__footer) { padding: 16px 24px calc(20px + env(safe-area-inset-bottom)); border-top: 1px solid var(--el-border-color-extra-light); }

.cc-drawer-body { display: grid; gap: 12px; }
.cc-setting {
  border: 1px solid var(--el-border-color);
  border-radius: 14px;
  padding: 12px;
  background: var(--el-fill-color-blank);
}
.cc-setting__row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.cc-setting__name { font-weight: var(--fw-bold); }
.cc-setting__desc { margin-top: 6px; font-size: var(--fs--2); opacity: 0.85; line-height: 1.45; }

.cc-drawer-actions { display: flex; gap: 10px; justify-content: flex-end; flex-wrap: wrap; padding-top: 6px; }

/* Mobile stacking */
@media (max-width: 720px) {
  .cc-grid { grid-template-columns: 1fr; }
  .cc-actions, .cc-drawer-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .cc-accept { grid-column: 1 / -1; grid-row: 1; }
}
@media (max-width: 600px) {
  .cc-modal-actions { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .cc-modal-actions :deep(.el-button) { width: 100%; padding: 0 10px; font-size: 12px; }
  .cc-modal-actions :deep(.cc-modal-accept) { grid-column: 1 / -1; grid-row: 1; height: 48px; font-size: 14px; }
  .cc-modal-actions :deep(.cc-modal-accept > span) { justify-content: space-between; width: 100%; padding: 0 6px; }
  .cc-modal-actions :deep(.cc-modal-settings) { grid-column: 2; grid-row: 2; }
  .cc-modal-actions :deep(.cc-modal-necessary) { grid-column: 1; grid-row: 2; }
}
@media (max-width: 480px) {
  :global(.cc-dialog) { border-radius: 20px; }
  :global(.cc-dialog .el-dialog__header) { padding: 26px 24px 20px; }
  :global(.cc-dialog .el-dialog__body) { padding: 0 24px 24px; }
  :global(.cc-dialog .el-dialog__footer) { padding: 20px 24px; }
  .cc-modal-heading { gap: 12px; }
  .cc-modal-symbol { flex-basis: 40px; width: 40px; height: 40px; }
  .cc-modal-title { font-size: 22px; }
  .cc-modal-copy { font-size: 14px; line-height: 1.75; }
}

/* Animations */
.cc-slide-enter-active, .cc-slide-leave-active { transition: transform 180ms ease, opacity 180ms ease; }
.cc-slide-enter-from, .cc-slide-leave-to { transform: translateY(12px); opacity: 0; }
</style>
